import {ChangeDetectionStrategy, Component, inject, input, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Button} from 'primeng/button';
import {InputTextarea} from 'primeng/inputtextarea';
import {Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {ApplicationService} from '../../services/application.service';
import {HttpErrorResponse} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {UpsertApplicationCommand} from 'admin-api-dto';

type ApplicationExample = {
  readonly title: string;
  readonly description: string;
  readonly payload: string;
};

const EXAMPLE_PAYLOADS: UpsertApplicationCommand[] = [
  {
    status: 'not-synced',
    lotteryId: 'lottery-not-synced',
    method: 'manual',
  },
  {
    status: 'applied',
    lotteryId: 'lottery-applied',
    nycId: 123456,
    nycStatus: 0,
    appliedAt: '2024-11-01T12:00:00.000Z',
    method: 'manual',
  },
  {
    status: 'pending-selection',
    lotteryId: 'lottery-pending',
    nycId: 789012,
    nycStatus: 0,
    applicationNumber: 555555,
    receivedApplicationNumberAt: '2024-11-02T12:00:00.000Z',
    method: 'manual',
  },
  {
    status: 'selected',
    lotteryId: 'lottery-selected',
    nycId: 789012,
    nycStatus: 0,
    applicationNumber: 987654,
    receivedApplicationNumberAt: '2024-11-02T12:00:00.000Z',
    selectionAt: '2024-11-10T09:30:00.000Z',
    method: 'manual',
  },
  {
    status: 'not-selected',
    lotteryId: 'lottery-not-selected',
    nycId: 789012,
    nycStatus: 0,
    applicationNumber: 123123,
    receivedApplicationNumberAt: '2024-11-02T12:00:00.000Z',
    selectionAt: '2024-11-10T09:30:00.000Z',
    reason: 'not-selected',
    method: 'manual',
  },
  {
    status: 'failed',
    lotteryId: 'lottery-failed',
    failureReason: 'Manually created application',
    appliedAt: '2024-11-01T12:00:00.000Z',
    method: 'manual',
  },
];

const DEFAULT_PAYLOAD = EXAMPLE_PAYLOADS[0];

@Component({
  selector: 'app-application-create-page',
  imports: [
    CommonModule,
    Button,
    InputTextarea,
  ],
  templateUrl: './application-create-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationCreatePageComponent {
  public readonly userId = input.required<string>();

  protected readonly examplePayload = JSON.stringify(DEFAULT_PAYLOAD, null, 2);
  protected readonly jsonInput = signal(JSON.stringify(DEFAULT_PAYLOAD, null, 2));
  protected readonly validationError = signal<string | undefined>(undefined);
  protected readonly loading = signal(false);
  protected readonly examples: ApplicationExample[] = EXAMPLE_PAYLOADS.map(example => ({
    title: example.status,
    description: `Create or replace a ${example.status} application`,
    payload: JSON.stringify(example, null, 2),
  }));

  private readonly router = inject(Router);
  private readonly applicationService = inject(ApplicationService);
  private readonly messageService = inject(MessageService);

  protected resetJson() {
    this.validationError.set(undefined);
    this.jsonInput.set(JSON.stringify(DEFAULT_PAYLOAD, null, 2));
  }

  protected goBack() {
    this.router.navigate(['/users', this.userId()]);
  }

  protected async saveApplication() {
    this.validationError.set(undefined);
    let parsedPayload: UpsertApplicationCommand;

    try {
      parsedPayload = JSON.parse(this.jsonInput()) as UpsertApplicationCommand;
    } catch {
      this.validationError.set('Provided value is not valid JSON.');
      return;
    }

    this.loading.set(true);

    try {
      await firstValueFrom(this.applicationService.upsertApplicationForUser(this.userId(), parsedPayload));
      this.messageService.add({
        severity: 'success',
        summary: 'Application saved',
        detail: 'Application stored for the user (overwrites if one existed for the lottery).',
      });
      this.resetJson();
    } catch (error) {
      this.validationError.set(this.getErrorMessage(error));
    } finally {
      this.loading.set(false);
    }
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const serverMessage = this.normalizeServerMessage(error.error);
      if (serverMessage) {
        return serverMessage;
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Unable to create application.';
  }

  private normalizeServerMessage(payload: unknown): string | undefined {
    if (!payload) {
      return undefined;
    }

    if (typeof payload === 'string') {
      return payload;
    }

    if (typeof payload === 'object' && 'message' in payload) {
      const value = (payload as { message?: unknown }).message;
      if (typeof value === 'string') {
        return value;
      }
    }

    return undefined;
  }
}
